import { useState } from "react";
import { Calendar, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LocationAutocomplete } from "@/components/common/PlaceAutocompleteInput";
import { FIND_RIDE_COUNTRY_CODE } from "@/features/find-ride/constants/findRide.constants";
import { savePendingFindRideSearch } from "@/features/find-ride/utils/pendingFindRideSearch";
import {
  arePlacesDifferent,
  isCompleteEasyModePlace,
} from "@/features/find-ride/utils/validateFindRidePlace";
import { toIsoDate, startOfDay } from "@/features/find-ride/utils/dateHelpers";
import type { EasyModePlaceSelection } from "@/types/easyModePlace";
import { ROUTE_PATHS } from "@/routes/routePaths";
import { cn } from "@/lib/utils";

type MarketingRideSearchBarProps = {
  /** Unique id prefix so multiple pages don't clash. */
  idPrefix: string;
  /** Accent for Search button — primary (available rides) or accent (ride requests). */
  buttonClassName?: string;
  className?: string;
};

/**
 * Pickup / Drop Places autocomplete + date + Search.
 * Search navigates to Find Ride with criteria prefilled (sessionStorage).
 */
export function MarketingRideSearchBar({
  idPrefix,
  buttonClassName,
  className,
}: MarketingRideSearchBarProps) {
  const navigate = useNavigate();
  const [pickup, setPickup] = useState<EasyModePlaceSelection | null>(null);
  const [drop, setDrop] = useState<EasyModePlaceSelection | null>(null);
  const [travelDate, setTravelDate] = useState(toIsoDate(startOfDay(new Date())));
  const [error, setError] = useState<string | null>(null);

  const handleSearch = () => {
    if (!isCompleteEasyModePlace(pickup) || !isCompleteEasyModePlace(drop)) {
      setError("Please select pickup and drop locations from the suggestions.");
      return;
    }
    if (!travelDate) {
      setError("Please select a travel date.");
      return;
    }
    if (!arePlacesDifferent(pickup, drop)) {
      setError("Pickup and drop must be different locations.");
      return;
    }

    setError(null);
    savePendingFindRideSearch({
      pickup,
      drop,
      date: travelDate,
      seats: 1,
    });
    navigate(`${ROUTE_PATHS.FIND_RIDE}?prefill=1`);
  };

  return (
    <div className={cn("mb-8", className)}>
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[1.2fr_1.2fr_0.9fr_auto]">
          <LocationAutocomplete
            id={`${idPrefix}-pickup`}
            label="Pickup Location"
            hideLabel
            placeholder="Pickup Location"
            mode="origin"
            restrictToCountryCode={FIND_RIDE_COUNTRY_CODE}
            value={pickup}
            onEasyModePlaceSelect={(place) => {
              setPickup(place);
              setError(null);
            }}
            onClear={() => setPickup(null)}
            onInteraction={() => setError(null)}
            pinClassName="h-4 w-4 text-[#22C55E]"
            inputClassName="h-auto rounded-lg border border-input bg-background py-2.5 text-sm"
            fieldClassName="w-full"
          />

          <LocationAutocomplete
            id={`${idPrefix}-drop`}
            label="Drop Location"
            hideLabel
            placeholder="Drop Location"
            mode="destination"
            restrictToCountryCode={FIND_RIDE_COUNTRY_CODE}
            value={drop}
            onEasyModePlaceSelect={(place) => {
              setDrop(place);
              setError(null);
            }}
            onClear={() => setDrop(null)}
            onInteraction={() => setError(null)}
            pinClassName="h-4 w-4 text-[#F59E0B]"
            inputClassName="h-auto rounded-lg border border-input bg-background py-2.5 text-sm"
            fieldClassName="w-full"
          />

          <div className="relative">
            <Calendar className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="date"
              className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring"
              value={travelDate}
              min={toIsoDate(startOfDay(new Date()))}
              onChange={(e) => {
                setTravelDate(e.target.value);
                setError(null);
              }}
              aria-label="Travel date"
            />
          </div>

          <button
            type="button"
            onClick={handleSearch}
            className={cn(
              "flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90",
              buttonClassName ?? "bg-primary"
            )}
          >
            <Search className="h-4 w-4" />
            Search
          </button>
        </div>

        {error ? (
          <p className="mt-3 text-sm text-red-600" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}
